/**
 * Progressive-disclosure proxy tools for capability tool sets.
 *
 * Background (see ADR-057):
 *
 * Capability authors can opt into deferred tool loading via
 * `defineCapability({ progressiveDisclosure: true })` (or
 * `defineMcpCapability({ progressiveDisclosure: true })`). When set, the
 * capability's tools are NOT placed in the flat model tool map. Instead,
 * `composeCapabilities` segregates them into a side map and injects two
 * always-available proxy tools:
 *
 *  - `discoverCapabilityTools({ capabilityId })` — returns the manifest
 *    `{ usage, prompt?, tools: [{name, description, parameters}, ...] }` for the
 *    requested capability so the model can read tool surfaces on demand.
 *  - `callCapabilityTool({ capabilityId, toolName, toolInputs })` —
 *    dispatches to the inner tool, wraps the result in an envelope so the
 *    chat UI's `CallCapabilityToolView` router can delegate to the inner
 *    tool's view via `renderToolPart(...)`.
 *
 * The factories return live AI SDK `Tool` objects directly (no
 * `defineTool` wrapping) because these are pure compose-time
 * infrastructure — they don't need ctx-gating, formatters, or any of the
 * other `DefinedTool` machinery.
 */

import type { JSONValue } from "@ai-sdk/provider";
import { tool as aiTool, type JSONSchema7, type Tool } from "ai";
import { z } from "zod";
import { toJsonSafe } from "../json-safe";
import type { ToolDecayConfig } from "../tool-formatters/decay-config";
import { shouldTrimByCacheSchedule } from "../tool-formatters/decay-helpers";
import { defaultToolFormatter } from "../tool-formatters/default";
import type {
  ToolFormatter,
  ToolFormatterContext,
  ToolPartDecision,
} from "../tool-formatters/define";
import { isOkEnvelope } from "../tool-formatters/ok-envelope";
import { withOpenRecords } from "./open-record-schema";

/** One inner tool's discovery descriptor. */
export type ProgressiveToolDescriptor = {
  name: string;
  description?: string;
  parameters: JSONSchema7;
};

/** The payload returned by `discoverCapabilityTools` for a given capability. */
export type ProgressiveCapabilityManifest = {
  /** Optional supplementary prompt from `cap.progressiveDisclosurePrompt`. */
  prompt?: string;
  tools: ProgressiveToolDescriptor[];
};

/**
 * Envelope shape returned by `callCapabilityTool`. The chat-UI router
 * (`CallCapabilityToolView`) reads this and synthesizes a
 * `ChatToolUIPart` for the inner tool, then delegates back through the
 * tool registry. Fields stay snake-free / short so they survive any
 * structured-content projection upstream.
 */
export type CallCapabilityToolEnvelope = {
  status: "ok" | "error";
  innerCapabilityId: string;
  innerToolName: string;
  innerInput: unknown;
  innerOutput?: unknown;
  error?: string;
};

/** Strict subset of AI SDK's `ToolResultOutput` covering the shapes the
 *  proxy's `toModelOutput` returns directly. Kept a subset (no `content`
 *  variant; `json`/`error-json` use `JSONValue`) so it stays cleanly
 *  assignable to the SDK type without a cast. Inner-tool results that use
 *  `content` are forwarded verbatim through the delegation path. */
type ToolResultOutput =
  | { type: "text"; value: string }
  | { type: "json"; value: JSONValue }
  | { type: "error-text"; value: string }
  | { type: "error-json"; value: JSONValue };

/** Inner tool's `toModelOutput`, as carried on the materialized AI SDK
 *  tool (forwarded verbatim by `defineTool`). */
type InnerToModelOutput = (options: {
  toolCallId: string;
  input: unknown;
  output: unknown;
}) => ToolResultOutput | PromiseLike<ToolResultOutput>;

/**
 * SDK-default tool-result projection — mirrors `createToolModelOutput`'s
 * fallback (ai/dist: `string → text`, else `json`) so a PD inner tool
 * with no `toModelOutput` lands exactly as a directly-registered one would.
 */
function defaultModelOutput(output: unknown): ToolResultOutput {
  return typeof output === "string"
    ? { type: "text", value: output }
    : { type: "json", value: (output ?? null) as JSONValue };
}

/**
 * Closure inputs for the proxy factories. The caller owns the maps;
 * `composeCapabilities` allocates them at compose time and recomposes on
 * each replica during workflow tool dispatch, so the closures stay
 * correctly bound.
 */
export type ProgressiveDisclosureInputs = {
  /** capabilityId → toolName → live AI SDK Tool */
  // biome-ignore lint/suspicious/noExplicitAny: tools carry per-tool I/O types
  pdTools: Map<string, Map<string, any>>;
  /** capabilityId → manifest (prompt + descriptors) */
  pdManifests: Map<string, ProgressiveCapabilityManifest>;
};

const DISCOVER_INPUT = z.object({
  capabilityId: z
    .string()
    .describe(
      "The id of a progressively-disclosed capability whose tools you want to inspect."
    ),
});

// `toolInputs` is a verbatim passthrough to the inner tool (whose own schema
// validates the contents), so it must stay an *open* object. AI SDK's converter
// would otherwise close it to `additionalProperties: false` and make the
// workflow's Ajv reject every forwarded arg — see `withOpenRecords`.
const CALL_INPUT = withOpenRecords(
  z.object({
    capabilityId: z
      .string()
      .describe(
        "Capability id (same value passed to discoverCapabilityTools)."
      ),
    toolName: z
      .string()
      .describe(
        "The unprefixed inner tool name as it appears in the discovered manifest."
      ),
    toolInputs: z
      .record(z.string(), z.unknown())
      .describe(
        "Arguments object matching the inner tool's input schema. Pass {} when the tool takes no arguments."
      ),
  })
);

const DISCOVER_DESCRIPTION =
  "Retrieve the on-demand tool catalog for a progressively-disclosed capability. Returns { usage, prompt?, tools: [{ name, description, parameters }, ...] }. Call this before invoking callCapabilityTool when you don't already know the tool surface from a prior turn.";

/**
 * Leads every discover result. Models reliably treat the manifest's tool
 * names as top-level tools and emit them bare, which the SDK rejects with
 * `AI_NoSuchToolError` (observed first-hand on concur-travel, 2026-10; same
 * imitation failure mode as ADR-057 §Negative). The dispatch rule therefore
 * rides IN the result the model is reading at the moment it plans the call,
 * not only in `callCapabilityTool`'s description.
 */
const manifestUsage = (capabilityId: string): string =>
  `The tools below are NOT directly callable. Invoke each one only via callCapabilityTool({ capabilityId: "${capabilityId}", toolName: "<name from this list>", toolInputs: { ...args matching its parameters } }).`;

const CALL_DESCRIPTION =
  "Invoke a tool that belongs to a progressively-disclosed capability. The capabilityId + toolName pair must match a tool from a prior discoverCapabilityTools call. toolInputs are forwarded verbatim to that inner tool; the response wraps the inner tool's output in an envelope so the UI can render the inner tool's card.";

/**
 * Drain an `AsyncIterable` return value to its terminal yield. AI SDK
 * tools may opt into streaming by returning an iterator from `execute`;
 * `executeTool` collapses those to the final yield via the same pattern.
 * The PD proxy needs the same drain because the envelope's `innerOutput`
 * is the durable record of the inner tool's result — an un-consumed
 * iterator there would silently strip every intermediate yield AND
 * serialize to `{}` when it crosses the workflow step boundary.
 *
 * Returns `value` unchanged when it is not an AsyncIterable.
 */
async function drainIfAsyncIterable(value: unknown): Promise<unknown> {
  if (
    value === null ||
    value === undefined ||
    typeof value !== "object" ||
    !(Symbol.asyncIterator in (value as object))
  ) {
    return value;
  }
  let last: unknown;
  for await (const next of value as AsyncIterable<unknown>) {
    last = next;
  }
  return last;
}

/**
 * Apply an inner tool's own `inputSchema` to the forwarded `toolInputs`.
 *
 * A directly-registered tool gets its args parsed by the AI SDK before
 * `execute` runs, so `z.default(...)` on an optional field materializes. The PD
 * proxy bypasses that: its `CALL_INPUT` validates only the envelope, and
 * `toolInputs` is an open record, so an inner tool used to receive the model's
 * raw object with every omitted-but-defaulted field still `undefined`. Tools
 * that then interpolate such a field into a request (`page: String(page)` →
 * `"undefined"`) sent malformed calls upstream — a silent, PD-only divergence
 * from the direct path (17 `.default()` declarations across the PD
 * capabilities were affected).
 *
 * Parsing here restores parity: defaults land, and a genuinely invalid arg is
 * reported as a validation error instead of reaching the inner tool. Schemas
 * that aren't Zod (e.g. AI SDK `jsonSchema()`) have no `safeParse`, so they
 * pass through untouched and keep the previous verbatim behavior.
 */
function applyInnerSchema(
  innerTool: unknown,
  toolInputs: unknown
): { ok: true; input: unknown } | { ok: false; error: string } {
  const schema = (
    innerTool as
      | { inputSchema?: { safeParse?: (value: unknown) => unknown } }
      | undefined
  )?.inputSchema;
  if (typeof schema?.safeParse !== "function") {
    return { ok: true, input: toolInputs };
  }
  const result = schema.safeParse(toolInputs) as {
    success: boolean;
    data?: unknown;
    error?: { message?: string };
  };
  if (result.success) {
    return { ok: true, input: result.data };
  }
  return {
    ok: false,
    error: `Invalid toolInputs: ${result.error?.message ?? "does not match the tool's input schema"}`,
  };
}

/**
 * Error for a `callCapabilityTool` naming a tool that isn't there. Lists
 * what is, so the model can correct the name in place — re-running
 * discovery would only re-pin the same manifest.
 */
function unknownToolError(
  pdManifests: ReadonlyMap<string, ProgressiveCapabilityManifest>,
  capabilityId: string,
  toolName: string
): string {
  const manifest = pdManifests.get(capabilityId);
  if (!manifest) {
    const ids = [...pdManifests.keys()].sort();
    return `Unknown capability "${capabilityId}". Progressively-disclosed capabilities available: ${ids.join(", ") || "none"}.`;
  }
  const names = manifest.tools.map((t) => t.name);
  return `Unknown tool "${toolName}" in capability "${capabilityId}". Its tools are: ${names.join(", ") || "none"}. Argument schemas are in the discoverCapabilityTools result for this capability.`;
}

/**
 * Append the inner tool's input schema to an error caused by its arguments,
 * so the model can fix the call without re-running discovery (whose earlier
 * result may have decayed, or been superseded and collapsed).
 */
function withInputSchema(
  error: string,
  pdManifests: ReadonlyMap<string, ProgressiveCapabilityManifest>,
  capabilityId: string,
  toolName: string
): string {
  const parameters = pdManifests
    .get(capabilityId)
    ?.tools.find((t) => t.name === toolName)?.parameters;
  if (!parameters) {
    return error;
  }
  return `${error}\nExpected toolInputs schema for ${toolName}: ${JSON.stringify(parameters)}`;
}

/** JSON-RPC "Invalid params" — how MCP servers reject a tool call's arguments. */
const JSON_RPC_INVALID_PARAMS = -32_602;

function isInvalidParamsError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { code?: unknown }).code === JSON_RPC_INVALID_PARAMS
  );
}

/**
 * Build the two proxy tools. Returns them as a `{ discover, call }` pair
 * so `composeCapabilities` can drop them into the flat tools map under
 * the conventional names.
 */
export function createProgressiveDisclosureTools(
  inputs: ProgressiveDisclosureInputs
): { discover: Tool; call: Tool } {
  const { pdTools, pdManifests } = inputs;

  const discover = aiTool({
    description: DISCOVER_DESCRIPTION,
    inputSchema: DISCOVER_INPUT,
    execute: async ({ capabilityId }) => {
      const manifest = pdManifests.get(capabilityId);
      if (!manifest) {
        return {
          error: "unknown_capability",
          requested: capabilityId,
          available: [...pdManifests.keys()].sort(),
        };
      }
      // `usage` first: the dispatch rule must be read before the tool list.
      return { usage: manifestUsage(capabilityId), ...manifest };
    },
  });

  const call = aiTool({
    description: CALL_DESCRIPTION,
    inputSchema: CALL_INPUT,
    // Forward HITL semantics from the inner tool. The serialization
    // boundary in workflow.ts currently strips `needsApproval`, so this
    // wiring is a no-op end-to-end today; it's correct here so the API
    // surface is ready when the HITL plumbing closes (see ADR-057
    // §Negative consequences). Runtime shape is `(input, options)`,
    // matching `ToolNeedsApprovalFunction` from `@ai-sdk/provider-utils`.
    needsApproval: async (input, options) => {
      const innerTool = pdTools.get(input.capabilityId)?.get(input.toolName);
      if (!innerTool) {
        return false;
      }
      const innerApproval = (innerTool as { needsApproval?: unknown })
        .needsApproval;
      if (innerApproval === undefined) {
        return false;
      }
      if (typeof innerApproval === "function") {
        return await (
          innerApproval as (
            innerInput: unknown,
            innerOptions: typeof options
          ) => boolean | Promise<boolean>
        )(input.toolInputs, options);
      }
      return Boolean(innerApproval);
    },
    execute: async (
      { capabilityId, toolName, toolInputs },
      execOpts
    ): Promise<CallCapabilityToolEnvelope> => {
      const innerTool = pdTools.get(capabilityId)?.get(toolName);
      if (!innerTool || typeof innerTool.execute !== "function") {
        return {
          status: "error",
          innerCapabilityId: capabilityId,
          innerToolName: toolName,
          innerInput: toolInputs,
          error: unknownToolError(pdManifests, capabilityId, toolName),
        };
      }

      // Parse through the inner tool's own schema so `.default()` fields
      // materialize exactly as they do for a directly-registered tool.
      const parsed = applyInnerSchema(innerTool, toolInputs);
      if (!parsed.ok) {
        return {
          status: "error",
          innerCapabilityId: capabilityId,
          innerToolName: toolName,
          innerInput: toolInputs,
          error: withInputSchema(
            parsed.error,
            pdManifests,
            capabilityId,
            toolName
          ),
        };
      }

      try {
        const raw = await innerTool.execute(parsed.input, execOpts);
        // Drain AsyncIterable inner tools (e.g. `bash`, future streaming
        // tools) so the envelope carries the terminal yield rather than
        // an un-consumed iterator object. Mirrors the drain in
        // `apps/web/workflows/chat/steps/execute-tool.ts` — without it,
        // any PD-enabled streaming tool would surface its iterator as
        // `innerOutput`, which serializes to `{}` and silently strips
        // every intermediate yield (ADR-057 §H3).
        //
        // Then JSON-roundtrip the drained value: the envelope becomes a
        // tool-result part in the next step's message history, and the AI
        // SDK validates that history against `modelMessageSchema`, whose
        // tool-result values must be pure JSON. A non-JSON value (e.g. a
        // Drizzle `Date`) survives the workflow journal intact and then
        // deterministically kills the turn with `AI_InvalidPromptError`
        // (2026-07-14 `devtools/feedback_search` incident). Inner tools
        // should serialize their own outputs; this is the backstop so no
        // future tool can reintroduce the failure. A tool output that
        // can't stringify at all (circular, BigInt) throws here and folds
        // into the catch below as a regular inner-tool error.
        const innerOutput = toJsonSafe(await drainIfAsyncIterable(raw));
        return {
          status: "ok",
          innerCapabilityId: capabilityId,
          innerToolName: toolName,
          innerInput: toolInputs,
          innerOutput,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return {
          status: "error",
          innerCapabilityId: capabilityId,
          innerToolName: toolName,
          innerInput: toolInputs,
          error: isInvalidParamsError(err)
            ? withInputSchema(message, pdManifests, capabilityId, toolName)
            : message,
        };
      }
    },
    // In-turn model projection — pure delegation to the inner tool's
    // `toModelOutput`, mirroring `formatForModel` delegation
    // (`createCallCapabilityToolFormatter`). The AI SDK applies this when
    // it folds the proxy's result into the next step's prompt
    // (`toResponseMessages({ tools })`), the same hook a directly-registered
    // tool gets — so a PD inner tool's `toModelOutput` runs identically
    // whether it was called directly or via the proxy. The envelope is
    // unwrapped to the inner `(input, output)`; absent an inner
    // `toModelOutput` we reproduce the SDK's own default so behavior is
    // byte-identical to a direct call.
    toModelOutput: async ({ toolCallId, output }) => {
      if (!output || typeof output !== "object") {
        return defaultModelOutput(output);
      }
      const envelope = output as CallCapabilityToolEnvelope;
      if (envelope.status === "error") {
        return {
          type: "error-text",
          value:
            envelope.error ??
            `Tool ${envelope.innerCapabilityId}/${envelope.innerToolName} failed`,
        };
      }
      const innerToModelOutput = pdTools
        .get(envelope.innerCapabilityId)
        ?.get(envelope.innerToolName)?.toModelOutput as
        | InnerToModelOutput
        | undefined;
      if (typeof innerToModelOutput === "function") {
        return await innerToModelOutput({
          toolCallId,
          input: envelope.innerInput,
          output: envelope.innerOutput,
        });
      }
      return defaultModelOutput(envelope.innerOutput);
    },
  });

  return { discover, call };
}

/** Conventional names — used by composeCapabilities AND the UI registry. */
export const PROGRESSIVE_DISCLOSURE_TOOL_NAMES = {
  discover: "discoverCapabilityTools",
  call: "callCapabilityTool",
} as const;

// ---------------------------------------------------------------------------
// Decay policy for the proxy tools (ADR-052 / ADR-053)
// ---------------------------------------------------------------------------
//
// The cache-aware decay framework runs `defaultToolFormatter` for any tool
// without a registered formatter. The default trims oversized outputs once
// `turnDelta >= 1` or on sweep ticks, replacing them with truncation references.
// That behavior is wrong for `discoverCapabilityTools`: the manifest is the
// model's only knowledge of which inner tools exist and what arguments they
// accept; collapsing it to a `recallToolResult` stub means every subsequent
// `callCapabilityTool` invocation needs an extra round-trip just to recover
// the surface it should still have in hand.
//
// `discoverCapabilityToolsFormatter` pins the manifest verbatim. The decay
// framework's lookup rule (`formatters.get(name) ?? defaultToolFormatter`)
// means registering any formatter at all suppresses the default — there's
// no need for a new "pin" flag on the framework side.
//
// The one exception is a manifest the model has since re-fetched for the
// same capability. Manifests are not small (Notion's is ~70 KB of argument
// schemas), and each re-discovery used to add another pinned copy for the
// rest of the chat. The newest copy is authoritative, so older ones collapse
// to a pointer at it — on the cache schedule, like any other decay.
//
// `callCapabilityTool` is intentionally NOT pinned. Each invocation carries
// one inner tool's output (MCP result rows, search hits, generated content)
// which can be large and is exactly what the default cache-aware decay
// schedule is designed to manage. Pinning the call envelope would defeat
// the purpose of having a decay framework at all.

/**
 * A superseded manifest is dead weight however old it is, so any tier the
 * schedule activates trims it (`baseline: 0`). The floor skips manifests
 * small enough that the cache break costs more than the bytes saved.
 */
const SUPERSEDED_MANIFEST_DECAY_CFG: ToolDecayConfig = {
  baseline: 0,
  minTrimSize: 2000,
  previewChars: 0,
};

/** Output a superseded manifest collapses to. */
type SupersededManifestStub = {
  superseded: true;
  note: string;
};

function isSupersededManifestStub(
  value: unknown
): value is SupersededManifestStub {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { superseded?: unknown }).superseded === true
  );
}

/**
 * Decay policy for `discoverCapabilityTools`: the manifest stays verbatim in
 * the prompt so the model can keep dispatching via `callCapabilityTool`
 * without re-discovery, until a later discovery of the same capability
 * lands — then this copy collapses to a {@link SupersededManifestStub}.
 */
export const discoverCapabilityToolsFormatter: ToolFormatter = (ctx) => {
  if (
    ctx.supersededBy === undefined ||
    ctx.output === undefined ||
    isSupersededManifestStub(ctx.output)
  ) {
    return {};
  }
  const trim = shouldTrimByCacheSchedule({
    charLen: ctx.outputCharLen,
    stepDelta: ctx.stepDelta,
    turnDelta: ctx.turnDelta,
    inTurnStepCount: ctx.inTurnStepCount,
    config: SUPERSEDED_MANIFEST_DECAY_CFG,
  });
  if (!trim) {
    return {};
  }
  const capabilityId = (ctx.args as { capabilityId?: unknown } | undefined)
    ?.capabilityId;
  const stub: SupersededManifestStub = {
    superseded: true,
    note: `Superseded by a later discoverCapabilityTools result for "${String(capabilityId)}" (toolCallId ${ctx.supersededBy}); use that one.`,
  };
  return { output: stub };
};

/**
 * Formatter contributions from the progressive-disclosure subsystem.
 *
 * Consumed by the chat workflow's `prepareStep` worker — see
 * `apps/web/workflows/chat/steps/process-prepare-step.ts::getFormatterMap`.
 * The map lives next to the proxy-tool factory so PD's decay policy stays
 * in the same module as the rest of its compose-time infrastructure.
 */
export const progressiveDisclosureFormatters: ReadonlyMap<
  string,
  ToolFormatter
> = new Map<string, ToolFormatter>([
  [
    PROGRESSIVE_DISCLOSURE_TOOL_NAMES.discover,
    discoverCapabilityToolsFormatter,
  ],
]);

/**
 * Cheap byte estimate for the inner sub-context's char-length fields.
 * Mirrors `applyToolFormatters`'s own JSON approximation so the inner
 * formatter sees budget signals consistent with a direct invocation.
 */
function jsonCharLen(value: unknown): number {
  if (value === undefined) {
    return 0;
  }
  try {
    const json = JSON.stringify(value);
    return typeof json === "string" ? json.length : 0;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/**
 * Generic output formatter for the `callCapabilityTool` proxy — pure
 * delegation, **no tool-specific logic**. This is the whole point of the
 * PD system: a progressively-disclosed tool's `formatForModel` must run
 * even when the model reached it through the proxy, in the SAME turn the
 * call was made. (Cross-turn already works: persistence rewrites the part
 * to the inner tool's namespaced identity per ADR-057, so the per-tool
 * formatter matches directly on later turns. In-turn the part is still
 * named `callCapabilityTool`, which is the gap this closes.)
 *
 * It unwraps the envelope to the inner tool's `(input, output)`, builds a
 * sub-context with inner-scoped char lengths — every other budget signal
 * (deltas, fill ratio, ids) passes through unchanged — and runs the inner
 * tool's formatter, falling back to {@link defaultToolFormatter} so a PD
 * inner tool with no custom formatter still gets cache-aware decay. The
 * inner decision is mapped back onto the proxy part: `output` rewrites the
 * envelope value the model sees; `input` is re-wrapped under the proxy's
 * `toolInputs`.
 *
 * `formatters` is the fully-resolved per-tool map keyed by the inner
 * tool's namespaced `${capabilityId}_${toolName}`. `getFormatterMap`
 * passes it in after the map is built so this closure can dispatch into it.
 */
export function createCallCapabilityToolFormatter(
  formatters: ReadonlyMap<string, ToolFormatter>
): ToolFormatter {
  return (ctx: ToolFormatterContext): ToolPartDecision => {
    const expansion = expandProgressiveDisclosureCall(
      PROGRESSIVE_DISCLOSURE_TOOL_NAMES.call,
      ctx.args,
      ctx.output
    );
    if (!expansion) {
      return {};
    }

    const inner =
      formatters.get(expansion.effectiveName) ?? defaultToolFormatter;
    const innerCtx: ToolFormatterContext = {
      ...ctx,
      toolName: expansion.effectiveName,
      args: expansion.effectiveInput,
      argsCharLen: jsonCharLen(expansion.effectiveInput),
      output: expansion.effectiveOutput,
      outputCharLen: jsonCharLen(expansion.effectiveOutput),
    };

    const decision = inner(innerCtx);
    if (!decision || typeof decision !== "object") {
      return {};
    }

    const out: ToolPartDecision = {};
    if (decision.output !== undefined) {
      out.output = decision.output;
    }
    if (decision.input !== undefined) {
      const proxyArgs =
        ctx.args && typeof ctx.args === "object"
          ? (ctx.args as Record<string, unknown>)
          : {};
      out.input = { ...proxyArgs, toolInputs: decision.input };
    }
    return out;
  };
}

// ---------------------------------------------------------------------------
// Outer→inner identity expansion
// ---------------------------------------------------------------------------
//
// The model emits tool-calls under the proxy name (`callCapabilityTool`), so
// every metric extractor and persisted ToolUIPart sees the proxy by default.
// That makes per-tool metric aggregation and history recall effectively
// blind to which underlying tool was invoked.
//
// The helpers below let those sites rewrite the proxy identity to the inner
// tool's namespaced name (`${capabilityId}_${toolName}`) plus its unwrapped
// input / output. Call them from *every* boundary between the AI SDK's
// emitted call/result events and our durable artifacts:
//
//   - `extractToolMetrics`        (chat_metric.tool_calls aggregation key)
//   - `persistMessages` /
//     `persistStreamError` /
//     `persistPartialAssistant`   (chat_messages.parts ToolUIPart.type)
//
// The UI's live-stream router (`CallCapabilityToolView`) does the same
// rewrite at render time for the proxy parts that arrive over the live
// stream before persistence — see `apps/web/components/tools/progressive-
// disclosure/call-view.tsx::synthesizeInnerPart`. These helpers and that
// renderer must stay structurally aligned (both use
// `${capabilityId}_${toolName}` and unwrap the same envelope), and the
// invariant is exercised by `expansion.test.ts`.

/**
 * Result of expanding a `callCapabilityTool` invocation into its inner
 * tool's identity, plus whatever sub-fields could be derived from the
 * supplied input/output.
 *
 * Returned by `expandProgressiveDisclosureCall`. `null` means "this was
 * not a PD proxy invocation, or required fields were missing — treat the
 * outer toolName/input/output verbatim".
 */
export type ProgressiveDisclosureExpansion = {
  /** Inner tool's namespaced name, e.g. `notion_search`. */
  effectiveName: string;
  /** Inner tool's args, sourced from the proxy input's `toolInputs` field
   *  when available, otherwise from the envelope's `innerInput`. `undefined`
   *  when neither is reachable. */
  effectiveInput: unknown;
  /** Inner tool's unwrapped output when the envelope reports success.
   *  `undefined` for the input-only path, error path, or pre-output state. */
  effectiveOutput: unknown;
  /** Error text when the envelope reports `status: "error"` (inner tool
   *  threw or unknown progressive tool). `undefined` otherwise. */
  effectiveErrorText: string | undefined;
  /** `true` iff this represents an inner-tool error reported via the
   *  envelope. Proxy-level dispatch errors are detected upstream (the
   *  ToolUIPart's own `state === "output-error"`) and are *not* signaled
   *  here — callers fold them in themselves. */
  isInnerError: boolean;
};

function readCallEnvelope(output: unknown): CallCapabilityToolEnvelope | null {
  if (!output || typeof output !== "object") {
    return null;
  }
  const o = output as Record<string, unknown>;
  if (o.status !== "ok" && o.status !== "error") {
    return null;
  }
  if (typeof o.innerCapabilityId !== "string" || !o.innerCapabilityId) {
    return null;
  }
  if (typeof o.innerToolName !== "string" || !o.innerToolName) {
    return null;
  }
  return {
    status: o.status as "ok" | "error",
    innerCapabilityId: o.innerCapabilityId,
    innerToolName: o.innerToolName,
    innerInput: o.innerInput,
    innerOutput: o.innerOutput,
    error: typeof o.error === "string" ? o.error : undefined,
  };
}

/** The inner tool's output when `output` is a `callCapabilityTool` envelope; else `output`. */
export function innerToolOutput(output: unknown): unknown {
  const envelope = readCallEnvelope(output);
  return envelope ? envelope.innerOutput : output;
}

/**
 * Whether a tool's *returned* value reports failure: a `{ ok: false, ... }`
 * result, or a `callCapabilityTool` envelope whose inner call errored or
 * itself returned one. Tools report expected failures this way instead of
 * throwing, so a caller that only watches for exceptions would record them
 * as successes.
 */
export function isFailedToolResult(output: unknown): boolean {
  const envelope = readCallEnvelope(output);
  if (envelope) {
    return (
      envelope.status === "error" || isFailedToolResult(envelope.innerOutput)
    );
  }
  return isOkEnvelope(output) && !output.ok;
}

/**
 * Resolve the inner tool's identity for a `callCapabilityTool` invocation,
 * pulling from whichever of `input` or `output` is available.
 *
 * `toolName` is the outer toolName as emitted by the AI SDK; non-proxy
 * names short-circuit to `null`. `input` is the proxy's args object
 * (`{ capabilityId, toolName, toolInputs }`) — preferred source for identity
 * because it's present even on pre-output / streaming states. `output` is
 * the proxy's return envelope; used as fallback identity source and as the
 * only source of unwrapped inner output / error text.
 *
 * Returns `null` when:
 *   - `toolName !== "callCapabilityTool"`, or
 *   - neither `input` nor `output` carry `capabilityId` + `toolName` (e.g.
 *     model is still streaming args and they haven't materialized).
 *
 * The function is pure and serializable; safe to call from any worker.
 */
export function expandProgressiveDisclosureCall(
  toolName: string,
  input?: unknown,
  output?: unknown
): ProgressiveDisclosureExpansion | null {
  if (toolName !== PROGRESSIVE_DISCLOSURE_TOOL_NAMES.call) {
    return null;
  }

  let effectiveName: string | undefined;
  let effectiveInput: unknown;
  if (input && typeof input === "object") {
    const i = input as {
      capabilityId?: unknown;
      toolName?: unknown;
      toolInputs?: unknown;
    };
    if (
      typeof i.capabilityId === "string" &&
      i.capabilityId.length > 0 &&
      typeof i.toolName === "string" &&
      i.toolName.length > 0
    ) {
      effectiveName = `${i.capabilityId}_${i.toolName}`;
      effectiveInput = i.toolInputs;
    }
  }

  let effectiveOutput: unknown;
  let effectiveErrorText: string | undefined;
  let isInnerError = false;
  if (output !== undefined) {
    const envelope = readCallEnvelope(output);
    if (envelope) {
      effectiveName ??= `${envelope.innerCapabilityId}_${envelope.innerToolName}`;
      if (effectiveInput === undefined) {
        effectiveInput = envelope.innerInput;
      }
      if (envelope.status === "error") {
        isInnerError = true;
        effectiveErrorText = envelope.error ?? "Tool failed";
      } else {
        effectiveOutput = envelope.innerOutput;
      }
    } else {
      // Output present but not a recognizable envelope. Pass it through
      // verbatim — caller is in better position to decide whether to keep
      // or drop it (e.g. journal-coordination's `unwrapToolPartOutput`
      // may already have collapsed it to a structured error shape).
      effectiveOutput = output;
    }
  }

  if (!effectiveName) {
    return null;
  }

  return {
    effectiveName,
    effectiveInput,
    effectiveOutput,
    effectiveErrorText,
    isInnerError,
  };
}

/**
 * Loose ToolUIPart shape — structural so callers can pass either the
 * `ai`-typed `ToolUIPart` or one of our nominally-typed `ChatToolUIPart`
 * variants without an explicit cast. Deliberately *no* index signature
 * (typed structs like `UIMessage` don't satisfy `{ [k: string]: unknown }`
 * even when their concrete property types are compatible).
 */
type LooseToolUIPart = {
  type: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

/**
 * If `part` is a `tool-callCapabilityTool` UI-part, return a structurally
 * identical part rewritten to the inner tool's identity:
 *
 *   - `type: "tool-callCapabilityTool"` → `type: "tool-${capId}_${name}"`
 *   - `input` is replaced with the envelope's `innerInput` (or the proxy's
 *     `toolInputs` for pre-output states).
 *   - On `state === "output-available"` with an inner-error envelope, the
 *     state is flipped to `"output-error"` with the inner error text in
 *     `errorText`, matching how the inner tool's own card would render had
 *     it been called directly (see ADR-057, §error semantics).
 *   - `output` is replaced with the envelope's `innerOutput` when present.
 *   - Pre-output states (`input-streaming` / `input-available`) and
 *     proxy-level errors (`state === "output-error"`) keep their state but
 *     still get the identity / input rewrite (eager rename per ADR-057).
 *
 * Returns the input verbatim when the part isn't a PD proxy invocation,
 * or when the identity can't be resolved (malformed/partial input).
 */
export function expandProgressiveDisclosureToolPart<P extends LooseToolUIPart>(
  part: P
): P {
  if (!part.type.startsWith("tool-")) {
    return part;
  }
  const outerToolName = part.type.slice("tool-".length);
  if (outerToolName !== PROGRESSIVE_DISCLOSURE_TOOL_NAMES.call) {
    return part;
  }

  // Only consult the output for terminal states — partial-stream states
  // don't yet have a meaningful envelope to unwrap.
  const hasOutput =
    part.state === "output-available" || part.state === "output-error";
  const expanded = expandProgressiveDisclosureCall(
    outerToolName,
    part.input,
    hasOutput ? part.output : undefined
  );
  if (!expanded) {
    return part;
  }

  const next: LooseToolUIPart = { ...part };
  next.type = `tool-${expanded.effectiveName}`;
  next.input = expanded.effectiveInput;

  if (part.state === "output-available") {
    if (expanded.isInnerError) {
      // Inner tool errored — flip the part to the error variant so the
      // inner tool's UI renders its own error card (ADR-057 §error policy:
      // 'treat as if inner tool were called normally').
      next.state = "output-error";
      next.errorText = expanded.effectiveErrorText ?? "Tool failed";
      delete next.output;
    } else {
      next.output = expanded.effectiveOutput;
    }
  } else if (part.state === "output-error") {
    // Proxy-level dispatch error. Keep the part's existing `errorText`
    // (the proxy's own message) — we don't have a more useful inner-tool
    // diagnostic to swap in. Identity + input are already rewritten so
    // metrics still attribute to the inner tool.
  }
  // input-streaming / input-available: identity + input rewritten only.

  return next as P;
}

/**
 * Loose UIMessage shape — accepts both `ai`'s `UIMessage` and our
 * `PersistedUIMessage` without requiring a dependency on either. Like
 * `LooseToolUIPart`, deliberately *no* index signature.
 */
type LooseUIMessage = {
  parts?: ReadonlyArray<{ type: string }>;
};

/**
 * Walk an array of UIMessages and rewrite every `tool-callCapabilityTool`
 * part inside to its inner-tool identity. Returns a new array iff any
 * part was rewritten; otherwise returns the original reference (so
 * downstream identity checks aren't disturbed in the common case).
 *
 * Used by the persistence steps (`persistMessages`, `persistStreamError`,
 * `persistPartialAssistant`) so the rows written to `chat_messages.parts`
 * carry the inner tool's namespaced `type` rather than the proxy's
 * (ADR-057 §persistence rewrite). On reload, the UI's tool-renderer
 * resolves the rewritten parts to the inner tool's registered view
 * directly, bypassing `CallCapabilityToolView` (which is only needed for
 * the live stream where the SDK still emits the proxy name).
 *
 * The internal `unknown` casts are necessary because TypeScript can't
 * reason about variant unions like `UIMessagePart` through the loose
 * structural bound; the runtime shape is checked guard-by-guard above.
 */
export function expandProgressiveDisclosureInMessages<M extends LooseUIMessage>(
  messages: ReadonlyArray<M>
): ReadonlyArray<M> {
  let mutated = false;
  const out: M[] = [];
  for (const msg of messages) {
    const parts = msg.parts;
    if (!parts || parts.length === 0) {
      out.push(msg);
      continue;
    }
    let partsMutated = false;
    const nextParts = parts.map((part) => {
      if (
        !part ||
        typeof part.type !== "string" ||
        !part.type.startsWith("tool-")
      ) {
        return part;
      }
      const rewritten = expandProgressiveDisclosureToolPart(
        part as unknown as LooseToolUIPart
      );
      if ((rewritten as unknown) !== (part as unknown)) {
        partsMutated = true;
      }
      return rewritten as unknown as typeof part;
    });
    if (partsMutated) {
      mutated = true;
      out.push({ ...msg, parts: nextParts } as M);
    } else {
      out.push(msg);
    }
  }
  return mutated ? out : messages;
}

// ---------------------------------------------------------------------------
// Inner→outer identity collapse (model-prompt view)
// ---------------------------------------------------------------------------
//
// The persistence rewrite above (`expandProgressiveDisclosure*`) is the right
// durable shape for the UI, metrics, and recall: a completed proxy call is
// stored under its inner `${capabilityId}_${toolName}` identity so reload
// renders the inner tool's card and per-tool aggregation attributes correctly.
//
// But that same persisted history is replayed into the *model* prompt on later
// turns, and the inner namespaced name is NOT a tool the model can actually
// emit — only `callCapabilityTool` / `discoverCapabilityTools` are in the tool
// set. Left as-is, the model sees its own prior calls labeled
// `quickbase_quickbaseQueryRecords`, imitates that name directly, and the SDK
// rejects it with `AI_NoSuchToolError` (the model then has to self-correct
// mid-turn). See ADR-057 §Negative and the 2026-07 Quickbase connector chats.
//
// `collapseProgressiveDisclosureToolPart` is the inverse transform, applied
// ONLY when assembling the model's view of history (never to what we persist).
// It rewrites `tool-${capId}_${toolName}` back to `tool-callCapabilityTool`
// with the proxy input shape `{ capabilityId, toolName, toolInputs }`, so the
// model sees an accurate, repeatable calling convention. The `output` /
// `errorText` / `state` are left untouched: the persisted inner output is
// already exactly what `callCapabilityTool.toModelOutput` projects for the
// model (the inner tool's output), so no envelope re-wrapping is needed.

/**
 * Collapse a persisted inner-identity tool part back to the
 * `callCapabilityTool` proxy identity for the model's prompt view.
 *
 * `pdCapabilityIds` is the set of capability ids that are progressively
 * disclosed for the current request; it's required to recover the
 * `capabilityId` / `toolName` split from the flat `${capabilityId}_${toolName}`
 * part type, since both halves may themselves contain underscores. The longest
 * matching known capability-id prefix wins so ids that share a prefix can't
 * mis-split.
 *
 * Returns the input verbatim when the part is not a `tool-*` part, is already
 * one of the proxy tools, or does not resolve to a known PD capability (e.g. a
 * regular top-level tool, or a capability that is no longer PD-enabled).
 */
export function collapseProgressiveDisclosureToolPart<
  P extends LooseToolUIPart,
>(part: P, pdCapabilityIds: ReadonlySet<string>): P {
  if (!part.type.startsWith("tool-")) {
    return part;
  }
  const name = part.type.slice("tool-".length);
  if (
    name === PROGRESSIVE_DISCLOSURE_TOOL_NAMES.call ||
    name === PROGRESSIVE_DISCLOSURE_TOOL_NAMES.discover
  ) {
    return part;
  }

  let capabilityId: string | undefined;
  for (const id of pdCapabilityIds) {
    if (
      name.length > id.length + 1 &&
      name.startsWith(id) &&
      name[id.length] === "_" &&
      (capabilityId === undefined || id.length > capabilityId.length)
    ) {
      capabilityId = id;
    }
  }
  if (capabilityId === undefined) {
    return part;
  }
  const toolName = name.slice(capabilityId.length + 1);

  const next: LooseToolUIPart = { ...part };
  next.type = `tool-${PROGRESSIVE_DISCLOSURE_TOOL_NAMES.call}`;
  next.input = {
    capabilityId,
    toolName,
    toolInputs:
      (part as { input?: unknown }).input &&
      typeof (part as { input?: unknown }).input === "object"
        ? (part as { input?: unknown }).input
        : {},
  };
  // output / errorText / state intentionally preserved — see module note.
  return next as P;
}

/**
 * Walk an array of UIMessages and collapse every persisted inner-identity PD
 * tool part back to the `callCapabilityTool` proxy identity for the model's
 * prompt view. Mirror of {@link expandProgressiveDisclosureInMessages};
 * returns a new array only when something changed so downstream identity
 * checks aren't disturbed in the common (no-PD) case.
 *
 * Applied in the history→model-message pipeline (see
 * `apps/web/workflows/chat/steps/load-context.ts::prepareMessagesForModel`) and
 * NOT at any persistence site — the durable rows keep their inner identity.
 */
export function collapseProgressiveDisclosureInMessages<
  M extends LooseUIMessage,
>(
  messages: ReadonlyArray<M>,
  pdCapabilityIds: ReadonlySet<string>
): ReadonlyArray<M> {
  if (pdCapabilityIds.size === 0) {
    return messages;
  }
  let mutated = false;
  const out: M[] = [];
  for (const msg of messages) {
    const parts = msg.parts;
    if (!parts || parts.length === 0) {
      out.push(msg);
      continue;
    }
    let partsMutated = false;
    const nextParts = parts.map((part) => {
      if (
        !part ||
        typeof part.type !== "string" ||
        !part.type.startsWith("tool-")
      ) {
        return part;
      }
      const rewritten = collapseProgressiveDisclosureToolPart(
        part as unknown as LooseToolUIPart,
        pdCapabilityIds
      );
      if ((rewritten as unknown) !== (part as unknown)) {
        partsMutated = true;
      }
      return rewritten as unknown as typeof part;
    });
    if (partsMutated) {
      mutated = true;
      out.push({ ...msg, parts: nextParts } as M);
    } else {
      out.push(msg);
    }
  }
  return mutated ? out : messages;
}
